import React, { FunctionComponent } from "react";
import { Form, Input } from "antd";
import type { Rule } from "antd/lib/form";
import FormLabel from "../FormLabel";
import styles from "../../BiformLayout.module.scss";
import { QUESTION_TYPES } from "../../constants";
import { buildRules } from "../../formUtils";
import { V2Question } from "../../helper";

// A website never holds a space, a quote, a backtick, an angle bracket or a backslash.
const FORBIDDEN_CHARACTERS = /[\s"'`<>\\]/;
// A leading scheme ("ftp:", "mailto:", "https:"); "acme.com:8080" is a host and port, not a scheme.
const SCHEME = /^[a-z][a-z0-9+.-]*:(?!\d)/i;
const HTTP_SCHEME = /^https?:\/\//i;
const IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;
const MAX_WEBSITE_LENGTH = 500;

// A named host with at least two labels, no empty label and a top-level label of 2+ characters that is not all digits.
const isWebsiteHostname = (hostname: string): boolean => {
  if (!hostname || hostname.startsWith("[") || IPV4.test(hostname)) return false;
  const labels = hostname.split(".");
  const last = labels[labels.length - 1];
  return labels.length >= 2 && labels.every(Boolean) && last.length >= 2 && !/^\d+$/.test(last);
};

/**
 * The website as a normalised http(s) URL, or null when it is not one. The same rule as core-api's and
 * tenant-admin's `toWebsiteUrl` (Rev 32): a bare domain ("acme.com", "www.acme.com/careers") gets "https://" in
 * front, the scheme and host are lowercased and a lone "/" path is dropped. This is only the learner-facing check;
 * core-api still decides what is stored on the internship (`placementMeta.internship.companyWebsite`).
 */
export const toCompanyWebsiteUrl = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text || FORBIDDEN_CHARACTERS.test(text)) return null;
  if (SCHEME.test(text) && !HTTP_SCHEME.test(text)) return null;
  let url: URL;
  try {
    url = new URL(SCHEME.test(text) ? text : `https://${text}`);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  // A user name or password ("hr@acme.com" read as a domain) never belongs in a company website.
  if (url.username || url.password) return null;
  if (!isWebsiteHostname(url.hostname)) return null;
  const result = `${url.protocol}//${url.host}${url.pathname === "/" ? "" : url.pathname}${url.search}${url.hash}`;
  return result.length > MAX_WEBSITE_LENGTH ? null : result;
};

/** The website question takes this input on a short text question, the type Form Builder seeds; any other type renders as built. */
export const usesCompanyWebsiteInput = (question?: V2Question): boolean => question?.questionType === QUESTION_TYPES.SHORT_TEXT;

/** The answer sent: what the learner typed, trimmed, or null when empty. core-api normalises it. */
export const toCompanyWebsiteAnswer = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value.trim() : null);

// The builder's default placeholder for a text question is "Enter", which says nothing about a website.
const resolvePlaceholder = (question: V2Question): string =>
  question.placeholder && question.placeholder !== "Enter" ? question.placeholder : "e.g. acme.com";

/**
 * The question's own required / length rules (a required question also refuses only spaces), then: when something
 * is typed, it must be a website. Empty is fine.
 */
const buildCompanyWebsiteRules = (question: V2Question): Rule[] => [
  ...(buildRules(question) as Rule[]).map(rule => ("required" in rule && rule.required ? { ...rule, whitespace: true } : rule)),
  {
    validator: (_, value) =>
      !toCompanyWebsiteAnswer(value) || toCompanyWebsiteUrl(value)
        ? Promise.resolve()
        : Promise.reject(new Error("Enter a website, like acme.com or https://acme.com"))
  }
];

interface CompanyWebsiteInputProps {
  // `id`, `value` and `onChange` are injected by the surrounding Form.Item.
  id?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  placeholder: string;
}

/** The text input; read-only views also show the saved website as a link that opens in a new tab. */
const CompanyWebsiteInput: FunctionComponent<CompanyWebsiteInputProps> = ({ id, value, onChange, disabled, placeholder }) => {
  const href = disabled ? toCompanyWebsiteUrl(value) : null;
  return (
    <>
      <Input id={id} value={value} onChange={onChange} disabled={disabled} placeholder={placeholder} inputMode="url" autoComplete="url" />
      {href && (
        <a href={href} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block break-all text-sm">
          Open website
        </a>
      )}
    </>
  );
};

interface CompanyWebsiteFieldProps {
  question: V2Question;
  disabled?: boolean;
}

/**
 * An off-campus request's Company website question: a text input that takes a full http(s) URL or a bare domain.
 * Optional unless the admin made the question required; a typed value that is not a website is flagged before submit.
 */
const CompanyWebsiteField: FunctionComponent<CompanyWebsiteFieldProps> = ({ question, disabled = false }) => (
  <Form.Item
    name={question.id}
    label={<FormLabel label={question.label} required={question.isRequired} description={question.description} />}
    rules={buildCompanyWebsiteRules(question)}
    // Read-only views show the saved answer like the other questions' read-only inputs, not greyed out.
    className={styles.disableTextInput}
  >
    <CompanyWebsiteInput disabled={disabled} placeholder={resolvePlaceholder(question)} />
  </Form.Item>
);

export default CompanyWebsiteField;
